import React, { useMemo, useEffect, useState, useCallback, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Skeleton,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemAvatar,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ICustomer, ICustomerUpsertDto } from "src/services/thumbz-base-api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { getServerErrorArrayMessage } from "src/utils/get-server-error-message";
import { useThumbzApi } from "src/services/thumbz-api";
import { queryKeys } from "src/config/query-key";
import PhotoForm from "src/components/upload/photo-form";
import { Box } from "@mui/system";
import { isPhotoBase64 } from "src/utils/is-base-64";
import PhoneNumberInput from "src/components/form/PhoneNumberInput";
import { MuiTelInputInfo } from "mui-tel-input";
import { PhoneNumberUtil } from "google-libphonenumber";
import { LoadingButton } from "@mui/lab";
import { RenderAvatar } from "src/components/avatar/RenderAvatar";

interface CustomerUpsertFormProps {
  open: boolean;
  customer: Partial<ICustomer> | null;
  wrk_id: string;
  onClose: () => void;
  onSuccess: (customer: ICustomer) => void;
  showSelect?: boolean;
  excludeCustomerIds?: string[];
}

interface ICustomerForm extends Omit<ICustomerUpsertDto, "wrk_id"> {
  base64?: string;
}

const phoneUtil = PhoneNumberUtil.getInstance();

export const CustomerUpsertForm: React.FC<CustomerUpsertFormProps> = ({
  open,
  customer,
  wrk_id,
  onClose,
  onSuccess,
  showSelect = true,
  excludeCustomerIds = [],
}) => {
  const [countryCode, setCountryCode] = useState<MuiTelInputInfo | null>(null);
  const [tab, setTab] = useState(showSelect ? 0 : 1);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const draftClearedRef = useRef(false);
  const hasLoadedDraftRef = useRef(false);
  const { api } = useThumbzApi();
  const queryClient = useQueryClient();
  const initialvalues: ICustomerForm = useMemo(
    () => ({
      cust_email: customer?.cust_email || "",
      cust_id: customer?.cust_id || null,
      cust_mobile_phone: customer?.cust_mobile_phone || "",
      cust_name: customer?.cust_name || "",
      country_calling_code: customer?.country_calling_code || "",
      base64: customer?.cust_photo?.ast_url || "",
      country_code: customer?.country_code || "",
    }),
    [customer],
  );
  const latestFormValuesRef = useRef<ICustomerForm>(initialvalues);
  const storageKey = useMemo(
    () => `customer-upsert-form-${wrk_id}-${customer?.cust_id || "new"}`,
    [customer?.cust_id, wrk_id],
  );

  useEffect(() => {
    latestFormValuesRef.current = initialvalues;
  }, [initialvalues]);

  const hasFormContent = useCallback((formValues: ICustomerForm) => {
    const fields: (string | null | undefined)[] = [
      formValues.cust_email,
      formValues.cust_mobile_phone,
      formValues.cust_name,
      formValues.country_calling_code,
      formValues.country_code,
      formValues.base64,
    ];

    return fields.some((field) => typeof field === "string" && field.trim() !== "");
  }, []);

  const clearDraft = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.removeItem(storageKey);
    draftClearedRef.current = true;
  }, [storageKey]);

  const saveDraft = useCallback(
    (values: ICustomerForm) => {
      if (typeof window === "undefined") {
        return;
      }

      if (!hasFormContent(values)) {
        window.localStorage.removeItem(storageKey);
        draftClearedRef.current = true;
        return;
      }

      const params = new URLSearchParams();

      params.set("cust_email", values.cust_email);
      params.set("cust_mobile_phone", values.cust_mobile_phone ?? "");
      params.set("cust_name", values.cust_name);
      params.set("country_calling_code", values.country_calling_code);
      params.set("country_code", values.country_code);
      params.set("base64", values.base64 || "");

      if (typeof values.cust_id === "string" && values.cust_id.length > 0) {
        params.set("cust_id", values.cust_id);
      }

      window.localStorage.setItem(storageKey, params.toString());
      draftClearedRef.current = false;
    },
    [hasFormContent, storageKey],
  );

  const loadDraft = useCallback((): ICustomerForm | null => {
    if (typeof window === "undefined") {
      return null;
    }

    const rawDraft = window.localStorage.getItem(storageKey);

    if (!rawDraft) {
      return null;
    }

    const params = new URLSearchParams(rawDraft);

    const draft: ICustomerForm = {
      cust_email: params.get("cust_email") || "",
      cust_id: params.get("cust_id"),
      cust_mobile_phone: params.get("cust_mobile_phone") || "",
      cust_name: params.get("cust_name") || "",
      country_calling_code: params.get("country_calling_code") || "",
      country_code: params.get("country_code") || "",
      base64: params.get("base64") || "",
    };

    if (draft.cust_id !== null && draft.cust_id.length === 0) {
      draft.cust_id = null;
    }

    if (!hasFormContent(draft)) {
      return null;
    }

    return draft;
  }, [hasFormContent, storageKey]);

  const allCustomers = useQuery({
    queryFn: () => api?.customer?.customerControllerList({ wrk_id }),
    queryKey: queryKeys.customer.customerControllerList({ wrk_id }).queryKey,
  });

  const filteredCustomers = useMemo(() => {
    const exclude = new Set(excludeCustomerIds);
    let customers = (allCustomers.data || []).filter((c) => !exclude.has(c.cust_id));

    if (search.trim()) {
      const s = search.trim().toLowerCase();
      customers = customers.filter(
        (c) => c.cust_name.toLowerCase().includes(s) || c.cust_email.toLowerCase().includes(s),
      );
    }

    return customers;
  }, [allCustomers.data, excludeCustomerIds, search]);

  const changeCustomerPhotoMutation = useMutation({
    mutationFn: api?.customer?.customerControllerUpdatePhoto,
    onSuccess: (res) => handleSuccess(res),
    onError: (error) =>
      getServerErrorArrayMessage(error).forEach((message) => toast.error(message)),
  });

  const upsertCustomerMutation = useMutation({
    mutationFn: api?.customer?.customerControllerUpsert,
    onSuccess: (res) => {
      if (isPhotoBase64(formik.values.base64))
        return changeCustomerPhotoMutation.mutate({
          base64: String(formik.values.base64),
          cust_id: res.cust_id,
        });
      else handleSuccess(res);
    },
    onError: (error) =>
      getServerErrorArrayMessage(error).forEach((message) => toast.error(message)),
  });

  const validationSchema = Yup.object().shape({
    cust_name: Yup.string().required("Nome é obrigatório"),
    cust_email: Yup.string().email("Email inválido").required("Email é obrigatório"),
    cust_mobile_phone: Yup.string()
      .required("Telefone é obrigatório")
      .test("valid-phone", "Telefone inválido", function (value) {
        try {
          const formCountryCode =
            typeof this.parent === "object" &&
            this.parent !== null &&
            "country_code" in this.parent &&
            typeof this.parent.country_code === "string"
              ? this.parent.country_code
              : "";

          const region = countryCode?.countryCode ?? formCountryCode;
          const regionCode = typeof region === "string" ? region : "";

          const parsedNumber = phoneUtil.parseAndKeepRawInput(value || "", regionCode);
          return phoneUtil.isValidNumber(parsedNumber);
        } catch {
          return false;
        }
      }),
  });

  const formik = useFormik<ICustomerForm>({
    initialValues: initialvalues,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      upsertCustomerMutation.mutate({
        cust_email: values.cust_email,
        cust_id: values.cust_id,
        cust_mobile_phone: values.cust_mobile_phone,
        cust_name: values.cust_name,
        country_calling_code: values.country_calling_code,
        country_code: countryCode?.countryCode || values.country_code || "",
        wrk_id: wrk_id,
      });
    },
  });

  const { resetForm, values } = formik;

  useEffect(() => {
    if (!open) {
      hasLoadedDraftRef.current = false;
      return;
    }

    if (hasLoadedDraftRef.current) {
      return;
    }

    const draft = loadDraft();
    const nextValues = draft
      ? {
          ...initialvalues,
          ...draft,
        }
      : initialvalues;

    resetForm({ values: nextValues });
    hasLoadedDraftRef.current = true;
  }, [initialvalues, loadDraft, open, resetForm]);

  useEffect(() => {
    hasLoadedDraftRef.current = false;
  }, [storageKey]);

  useEffect(() => {
    latestFormValuesRef.current = values;

    if (draftClearedRef.current && hasFormContent(values)) {
      draftClearedRef.current = false;
    }
  }, [hasFormContent, values]);

  const prevOpenRef = useRef(open);

  useEffect(() => {
    if (!open && prevOpenRef.current) {
      if (draftClearedRef.current) {
        draftClearedRef.current = false;
      } else {
        saveDraft(latestFormValuesRef.current);
      }
    }

    if (open && !prevOpenRef.current) {
      draftClearedRef.current = false;
    }

    prevOpenRef.current = open;
  }, [open, saveDraft]);

  function handleSuccess(customer: ICustomer) {
    clearDraft();
    resetForm({ values: initialvalues });
    setCountryCode(null);

    toast.success("Aprovador salvo com sucesso");

    queryClient.invalidateQueries({
      queryKey: queryKeys.customer.customerControllerList({
        wrk_id,
      }).queryKey,
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.customer.customerControllerFind(customer.cust_id).queryKey,
    });

    if (onSuccess) {
      onSuccess(customer);
    }
  }

  function handleAddExisting() {
    selectedIds.forEach((id) => {
      const customer = allCustomers.data?.find((c) => c.cust_id === id);
      if (customer) {
        onSuccess(customer);
      }
    });
    setSelectedIds([]);
    setSearch("");
    onClose();
  }

  const toggleCustomer = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  };

  useEffect(() => {
    if (!countryCode) {
      return;
    }

    formik.setFieldValue("country_calling_code", countryCode.countryCallingCode || "");
    formik.setFieldValue("country_code", countryCode.countryCode || "");
    formik.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode]);

  function handleResetForm() {
    clearDraft();
    resetForm({ values: initialvalues });
    setCountryCode(null);
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      {showSelect && (
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          aria-label="add approver tabs"
          sx={{ px: 3.5, pt: 1 }}
        >
          <Tab label="Aprovadores Existentes" value={0} />
          <Tab label="Novo Aprovador" value={1} />
        </Tabs>
      )}

      {showSelect && tab === 0 && (
        <>
          <DialogContent>
            <Stack spacing={2}>
              <TextField
                placeholder="Buscar por nome ou email"
                fullWidth
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <List sx={{ maxHeight: 300, overflow: "auto" }}>
                {filteredCustomers.length > 0 ? (
                  filteredCustomers.map((customer) => (
                    <ListItem
                      key={customer.cust_id}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCustomer(customer.cust_id);
                      }}
                      secondaryAction={
                        <Checkbox
                          edge="end"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCustomer(customer.cust_id);
                          }}
                          checked={selectedIds.includes(customer.cust_id)}
                        />
                      }
                    >
                      <ListItemAvatar>
                        <RenderAvatar
                          src={customer.cust_photo?.ast_url}
                          name={customer.cust_name}
                          variant="circular"
                        />
                      </ListItemAvatar>
                      <ListItemText primary={customer.cust_name} secondary={customer.cust_email} />
                    </ListItem>
                  ))
                ) : (
                  <Typography sx={{ p: 2 }} align="center">
                    Nenhum aprovador encontrado
                  </Typography>
                )}
              </List>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Cancelar</Button>
            <Button onClick={handleAddExisting} disabled={selectedIds.length === 0} color="primary">
              Adicionar
            </Button>
          </DialogActions>
        </>
      )}

      {(!showSelect || tab === 1) && (
        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <Box display={upsertCustomerMutation.isPending ? "none" : "block"}>
              <PhotoForm
                key={`${formik.values.base64}-${Date.now()}`}
                loading={changeCustomerPhotoMutation.isPending}
                default_photo_url={formik.values.base64}
                onFormSuccessSubmit={({ base64 }) => {
                  formik.setFieldValue("base64", base64);
                }}
              />
            </Box>
            {upsertCustomerMutation.isPending && (
              <Skeleton variant="rectangular" width="50" height={100} />
            )}

            <Box my={2}></Box>

            <TextField
              autoFocus
              margin="dense"
              label="Nome"
              type="text"
              fullWidth
              {...formik.getFieldProps("cust_name")}
              error={Boolean(formik.errors.cust_name) && formik.touched.cust_name}
              helperText={formik.errors.cust_name}
              disabled={upsertCustomerMutation.isPending}
            />
            <TextField
              margin="dense"
              label="Email"
              type="email"
              placeholder="jose@gmail.com"
              fullWidth
              {...formik.getFieldProps("cust_email")}
              error={Boolean(formik.errors.cust_email) && formik.touched.cust_email}
              helperText={formik.errors.cust_email}
              disabled={upsertCustomerMutation.isPending}
            />
            <PhoneNumberInput
              field={formik.getFieldProps("cust_mobile_phone")}
              form={formik}
              label="Telefone"
              meta={formik.getFieldMeta("cust_mobile_phone")}
              key={"cust_mobile_phone"}
              setCountryCode={setCountryCode}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleResetForm}
              color="inherit"
              disabled={upsertCustomerMutation.isPending}
            >
              Resetar
            </Button>
            <Button onClick={onClose} color="primary" disabled={upsertCustomerMutation.isPending}>
              Cancelar
            </Button>
            <LoadingButton type="submit" color="primary" loading={upsertCustomerMutation.isPending}>
              Adicionar
            </LoadingButton>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};
